/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatPage, ChatProvider } from '@silainsights/chat-components';
import type { Message as LibMessage } from '@silainsights/chat-components';
import { useTheme } from '../contexts/ThemeContext';
import { useChatContext } from '../contexts/ChatContext';
import {
  getConversationsApiV3ConversationsGetOptions,
  getConversationsApiV3ConversationsGetQueryKey,
} from '../config/api/@tanstack/react-query.gen';
import type { ConversationSummary } from '../config/api/types.gen';

const LandingChatPage = () => {
  const queryClient = useQueryClient();
  const { isDark } = useTheme();
  const {
    conversationId,
    messages,
    sendMessage,
    selectConversation,
    startNewConversation,
    isLoading,
    isLoadingMessages,
  } = useChatContext();
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('Mayar');
  const settingsRef = React.useRef<HTMLDivElement>(null);

  // Load conversations for sidebar
  const conversationsQuery = useQuery({
    ...getConversationsApiV3ConversationsGetOptions(),
  });
  const recentChats: ConversationSummary[] = conversationsQuery.data ?? [];
  const isLoadingChats = conversationsQuery.isLoading;

  // Rotating placeholder texts
  const placeholders = [
    "Ask me about brand health metrics...",
    "Explore traditional vs digital insights...",
    "Discover brand performance trends..."
  ];

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteChat = (chatIdToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.warn('Delete not implemented yet — no backend DELETE endpoint', chatIdToDelete);
  };

  // Rotate placeholders
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [placeholders.length]);

  const handleSendMessage = (input: string) => {
    void sendMessage(input);
  };

  const handleSuggestionClick = (query: string) => {
    handleSendMessage(query);
  };

  const clearChat = () => {
    startNewConversation();
  };

  const hasMessages = messages.length > 0;

  const filteredChats = recentChats.filter(chat =>
    (chat.title ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChatSelect = (selectedChatId: string) => {
    setSidebarOpen(false);
    selectConversation(selectedChatId);
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: getConversationsApiV3ConversationsGetQueryKey(),
    });
  };

  // Sidebar Panel Component
  const ChatSidebar = () => (
    <>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Chats</h2>
            <div className="flex items-center gap-2">
              {/* Settings Dropdown */}
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                  title="Settings"
                >
                  <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                {settingsOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="py-1">
                      <p className="px-3 py-2 text-xs text-text-muted font-medium uppercase tracking-wider">Select Client</p>
                      {['Mayar', 'Saib', 'Coop', 'Magrabi'].map((client) => (
                        <button
                          key={client}
                          onClick={() => {
                            setSelectedClient(client);
                            setSettingsOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            selectedClient === client
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground hover:bg-background-secondary'
                          }`}
                        >
                          {selectedClient === client && (
                            <svg className="w-3 h-3 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {client}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleRefresh}
                disabled={isLoadingChats}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                title="Refresh"
              >
                <svg className={`w-4 h-4 text-text-muted ${isLoadingChats ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* New Chat Button */}
          <div className="p-4">
            <button
              onClick={() => {
                clearChat();
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="font-medium">New chat</span>
            </button>
          </div>

          {/* Search */}
          <div className="px-4 pb-4">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search your chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 bg-background-secondary border border-border rounded-xl text-sm text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-foreground"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Recent Chats */}
          <div className="flex-1 overflow-y-auto px-4">
            <p className="text-xs text-text-muted font-medium uppercase tracking-wider mb-3">
              Recents {recentChats.length > 0 && `(${recentChats.length})`}
            </p>

            {isLoadingChats ? (
              <div className="flex justify-center py-8">
                <svg className="w-6 h-6 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <div
                      key={chat.id}
                      onClick={() => handleChatSelect(chat.id)}
                      className={`relative group px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                        chat.id === conversationId
                          ? 'bg-primary/10 border border-primary/20'
                          : 'hover:bg-background-secondary'
                      }`}
                    >
                      <div className="pr-8">
                        <p className={`text-sm truncate transition-colors ${
                          chat.id === conversationId
                            ? 'text-primary font-medium'
                            : 'text-foreground group-hover:text-primary'
                        }`}>
                          {chat.title || 'New conversation'}
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          {formatRelativeTime(chat.updated_at)}
                        </p>
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDeleteChat(chat.id, e)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Delete conversation"
                      >
                        <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm text-text-muted">
                      {searchQuery ? 'No chats match your search' : 'No conversations yet'}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => {
                          clearChat();
                          setSidebarOpen(false);
                        }}
                        className="mt-3 text-sm text-primary hover:underline"
                      >
                        Start your first chat
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Sidebar Toggle Button Component
  const SidebarToggleButton = ({ className = '' }: { className?: string }) => (
    <button
      onClick={() => setSidebarOpen(true)}
      className={`p-2 hover:bg-background-secondary border border-border rounded-lg transition-colors ${className}`}
      title="Open chat history"
    >
      <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
        <rect x="15" y="3" width="6" height="18" rx="1" stroke="currentColor" strokeWidth={1.5} fill="none" />
      </svg>
    </button>
  );

  // Convert MessageOut to library format
  const libMessages: LibMessage[] = messages.map((msg) => ({
    id: msg.id,
    role: msg.role as 'user' | 'assistant',
    content: msg.content ?? '',
    timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
  }));

  // Welcome screen logo
  const WelcomeLogo = (
    <div className="flex flex-col items-center">
      <div className="w-40 h-14 relative flex items-center justify-center mb-0">
        <img
          src="/sila-wordmark.png"
          alt="Sila Logo"
          className="w-full h-full object-contain"
        />
        <span className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase shadow-sm">
          beta
        </span>
      </div>
    </div>
  );

  // Header actions for ChatPage
  const HeaderActions = (
    <div className="flex items-center space-x-2">
      <button
        onClick={clearChat}
        className="px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors text-sm"
      >
        <i className="fas fa-plus mr-2"></i>
        New Chat
      </button>
      <SidebarToggleButton />
    </div>
  );

  // Suggestions for welcome screen
  const welcomeSuggestions = [
    "What is my brand's health score?",
    "Compare traditional vs digital metrics",
    "Show demographic insights",
  ];

  // Chat view - using ChatPage component
  return (
    <ChatProvider
      mode={isDark ? 'dark' : 'light'}
      theme={{
        colors: {
          primary: '#2563EB',
          userBubble: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
        },
      }}
      features={{
        streaming: true,
        progressIndicator: true,
        markdown: true,
      }}
    >
      {/* Chat Sidebar */}
      <ChatSidebar />

      <ChatPage
        messages={libMessages}
        showWelcome={!hasMessages && !isLoadingMessages}
        welcomeGreeting="BrandHealth 360"
        welcomeTagline="AI-powered brand health insights and analysis"
        welcomeLogo={WelcomeLogo}
        welcomeSuggestions={welcomeSuggestions}
        inputPlaceholder={placeholders[placeholderIndex]}
        inputDisabled={isLoading || isLoadingMessages}
        isStreaming={isLoading || isLoadingMessages}
        streamStage={isLoadingMessages ? "searching" : "analyzing"}
        onSendMessage={handleSendMessage}
        onSuggestionClick={handleSuggestionClick}
        disclaimer="BrandHealth 360 provides AI-powered analytics based on available data."
        title={hasMessages ? 'BrandHealth 360' : undefined}
        headerActions={hasMessages ? HeaderActions : undefined}
      />

      {/* Sidebar Toggle for welcome screen */}
      {!hasMessages && (
        <div className="fixed top-4 right-4 z-30">
          <SidebarToggleButton />
        </div>
      )}
    </ChatProvider>
  );
};

export default LandingChatPage;
